// Stock count schedule and count owners (CR_20260914) — the rules web and mobile share. The database decides
// (app.count_due_on / count_next_due, public.count_status()); the due-date rule is mirrored here only to preview
// the next counts while a manager edits a schedule, and a test pins both on the same cases.

/** `YYYY-MM-DD` dates in the company timezone, as the database returns them. */
export type IsoDate = string;

export interface CountSchedule {
  every_days: number | null;
  start_date: IsoDate | null;
  /** ISO weekdays, 1 = Monday … 7 = Sunday */
  skip_days: number[];
}

const DAY_MS = 86_400_000;
const toUtc = (d: IsoDate) => Date.parse(`${d}T00:00:00Z`);
const fromUtc = (ms: number): IsoDate => new Date(ms).toISOString().slice(0, 10);

export const addDays = (d: IsoDate, n: number): IsoDate => fromUtc(toUtc(d) + n * DAY_MS);
export const daysBetween = (from: IsoDate, to: IsoDate): number =>
  Math.round((toUtc(to) - toUtc(from)) / DAY_MS);
/** 1 = Monday … 7 = Sunday */
export const isoWeekday = (d: IsoDate): number => ((new Date(toUtc(d)).getUTCDay() + 6) % 7) + 1;

/** Same rule as app.count_due_on: on or after the start, a whole number of cycles on, not a skip day. */
export function countDueOn(s: CountSchedule, day: IsoDate): boolean {
  if (!s.every_days || !s.start_date) return false;
  const n = daysBetween(s.start_date, day);
  return n >= 0 && n % s.every_days === 0 && !s.skip_days.includes(isoWeekday(day));
}

/** The next `count` due dates on or after `from` (the "Next counts" preview). */
export function nextDueDates(s: CountSchedule, from: IsoDate, count = 3): IsoDate[] {
  if (!s.every_days || !s.start_date) return [];
  const out: IsoDate[] = [];
  const n = daysBetween(s.start_date, from);
  let d = n <= 0 ? s.start_date : addDays(s.start_date, Math.ceil(n / s.every_days) * s.every_days);
  // a cycle that isn't a multiple of 7 reaches every weekday within 7 steps
  for (let i = 0; out.length < count && i < count * 8; i++, d = addDays(d, s.every_days))
    if (!s.skip_days.includes(isoWeekday(d))) out.push(d);
  return out;
}

/** A schedule the database would refuse (sites_count_skip_days_ck): no due date would ever come. */
export function neverDue(s: CountSchedule): boolean {
  if (!s.every_days || !s.start_date) return false;
  if (s.skip_days.length >= 7) return true;
  return s.every_days % 7 === 0 && s.skip_days.includes(isoWeekday(s.start_date));
}

/** The repeat picker: daily, every 2 days, weekly, or any other number of days. */
export type Repeat = 'none' | 'daily' | 'every2' | 'weekly' | 'custom';
export function repeatOf(everyDays: number | null): Repeat {
  if (!everyDays) return 'none';
  return everyDays === 1
    ? 'daily'
    : everyDays === 2
      ? 'every2'
      : everyDays === 7
        ? 'weekly'
        : 'custom';
}

// ---------------------------------------------------------------------------------------------------------
// count_status() rows
// ---------------------------------------------------------------------------------------------------------
export type CountState =
  'not_scheduled' | 'upcoming' | 'due' | 'in_progress' | 'done' | 'done_late' | 'overdue';

export interface CountOwner {
  user_id: string;
  name: string | null;
  /** false: named owner who no longer has access to the site (no longer counts as owner) */
  has_access: boolean;
}

export interface LastCount {
  id: string;
  closed_at: string;
  closed_by: string | null;
  closed_by_name: string | null;
  scheduled_for: IsoDate | null;
  expected: number;
  found: number;
  not_found: number;
  unverified: number;
  unexpected: number;
  /** people other than the closer who ticked lines */
  helpers: number;
}

export interface OpenCount {
  id: string;
  created_at: string;
  created_by_name: string | null;
  not_found: number;
  unverified: number;
  contributors: { user_id: string | null; name: string | null; n: number }[];
}

export interface CountStatus {
  site_id: string;
  site_name: string;
  site_type: 'yard' | 'warehouse' | 'project_site' | 'customer_site';
  every_days: number | null;
  start_date: IsoDate | null;
  due_time: string;
  remind_time: string;
  skip_days: number[];
  owners: CountOwner[];
  can_count: boolean;
  today: IsoDate;
  next_due: IsoDate | null;
  state: CountState;
  previous_missed: IsoDate | null;
  last_count: LastCount | null;
  open_count: OpenCount | null;
}

/** Decision C11: all accounted for = nothing not found and no QR-only asset left unchecked. */
export function countResult(c: Pick<LastCount, 'not_found' | 'unverified' | 'unexpected'>): {
  ok: boolean;
  missing: number;
  unexpected: number;
} {
  const missing = c.not_found + c.unverified;
  return { ok: missing === 0, missing, unexpected: c.unexpected };
}

export type Tone = 'success' | 'warning' | 'error' | 'info' | 'default';
export const STATE_TONE: Record<CountState, Tone> = {
  done: 'success',
  done_late: 'warning',
  due: 'info',
  in_progress: 'info',
  overdue: 'error',
  upcoming: 'default',
  not_scheduled: 'default',
};

/**
 * A count left open past the day it was opened (owner, 2026-09-18). It matters more than it looks: one open count
 * per site is a hard rule, so a forgotten count stops the place being counted at all, and its evidence window keeps
 * widening until somebody closes it. `today` and `openedOn` are calendar dates in the company timezone.
 */
export function staleCount(openedOn: IsoDate, today: IsoDate): { stale: boolean; days: number } {
  const days = daysBetween(openedOn, today);
  return { stale: days >= 1, days };
}

/** Owners who still count as owners (C4). */
export const activeOwners = (s: Pick<CountStatus, 'owners'>) =>
  s.owners.filter((o) => o.has_access);

/**
 * Phone count screen (C9): tagged not-found lines whose tag the phone just heard, not ticked or queued yet. QR-only
 * lines are never heard (no electronic tag), so they stay for a scan or a visual tick.
 */
export function assetsToTick(
  lines: { asset_id: string; bucket: string; cleared_at: string | null }[],
  heardAssetIds: Iterable<string>,
  alreadyTicked: ReadonlySet<string>,
): string[] {
  const heard = new Set(heardAssetIds);
  return lines
    .filter(
      (l) =>
        l.bucket === 'not_found' &&
        !l.cleared_at &&
        heard.has(l.asset_id) &&
        !alreadyTicked.has(l.asset_id),
    )
    .map((l) => l.asset_id);
}
